import {
  ApiDataQuery,
  AuditAudio,
  AuditLiveMention,
  AuditMatch,
  AuditSchedule,
  BaseAPIClient,
  CreatePntsDto,
  CreateScheduleDtoV2,
  DateStr,
  DateTimeStr,
  EqualMd5AudiosReportDto,
  GetAuditConfigDto,
  GetPntScheduleDto,
  GipReportParametersForOrderIdResult,
  GlobalNoMatchesReportDto,
  GlobalSchedulesNoMatchesReport,
  Milliseconds,
  MissingAudiosReportDto,
  NoMatchesReport,
  Paginated,
  PntStationStatusReportDtoV2,
  ScheduleStatusReport,
  toDateTimeStr,
  UpdateAuditConfigDto,
} from '@mi-front/core';
import { AxiosProgressEvent } from 'axios';

export interface FetchMatchesFilter {
  readonly from: DateTimeStr;
  readonly to: DateTimeStr;
  readonly customId?: string | string[];
}

export interface CreateManualMatchDto {
  audioId: string;
  datetime: DateTimeStr;
  score?: number;
  playedDuration?: Milliseconds;
}

export interface CreateLiveMentionManualDto {
  datetime: DateTimeStr;
  name: string;
  description: string;
}

export interface AdsType {
  start_time: number;
  image_b64: Base64URLString;
  end_time: number;
  brand: string;
}
export interface AdoverlayRange {
  filename: string;
  processing: number;
  ad_overlay_groups: {
    duration: number;
    start_time: number;
    ads: AdsType[];
  }[];
  date: string;
}

/** Lowerthird detection returned for a station time range. */
export interface LowerthirdRangeItem {
  text: string;
  from: DateTimeStr;
  to: DateTimeStr;
}

/** Screenbug detection returned for a station time range. */
export interface ScreenbugRangeItem {
  screen_bug: string;
  from: DateTimeStr;
  to: DateTimeStr;
}

/** Viewer counts keyed by channel id at a single sample time. */
export interface YoutubeStatsChannelData {
  [channelId: string]: number;
}

/** One sample from the legacy yt-data viewers endpoint. */
export interface YoutubeStatsTimePoint {
  time: string;
  channelId: YoutubeStatsChannelData[];
}

/** Channel metadata for a country from the legacy yt-data channels endpoint. */
export interface YoutubeChannelMetadata {
  channelName: string;
  liveUrl: string;
  channelId: string;
  countryCode: string;
  category: string;
}

export const AuditAPI = {
  fetchMatches(
    this: BaseAPIClient,
    args: ApiDataQuery<{
      stationId: string;
      filter: FetchMatchesFilter;
    }>,
  ): Promise<Paginated<AuditMatch>> {
    const { stationId, filter, page, pageSize, sort } = args;
    const { from, to, customId } = filter;

    return this.get(`/v2/stations/${stationId}/audit/results`, {
      params: {
        sort,
        page,
        pageSize,
        from,
        to,
        customId,
      },
    });
  },

  async fetchMatchesByCustomId(
    this: BaseAPIClient,
    args: ApiDataQuery<{
      stationId: string;
      filter: {
        from?: DateTimeStr | null;
        to?: DateTimeStr | null;
        name?: string | string[];
        nameLike?: string;
      };
    }>,
  ): Promise<Paginated<{ id: string; name: string }>> {
    const { stationId, filter, ...query } = args;
    const { from, to, name, nameLike } = filter;

    return this.get(`/v2/stations/${stationId}/audit/live-mentions/matches`, {
      params: {
        ...query,
        pageSize: 999,
        select: query.select ? query.select.join(',') : undefined,
        filter: {
          name,
          'name.like': nameLike,
          'createdAt.>=': from,
          'createdAt.<=': to,
        },
      },
    });
  },

  fetchLiveMentionsMatches(
    this: BaseAPIClient,
    args: ApiDataQuery<{
      stationId: string;
      filter: {
        from: DateTimeStr;
        to: DateTimeStr;
      };
    }>,
  ): Promise<Paginated<AuditLiveMention>> {
    const { stationId, filter, pageSize, page, sort } = args;
    const { from, to, name } = filter;
    let url = `/v2/stations/${stationId}/audit/live-mentions/matches`;

    if (name) {
      const repeatedNames = name
        .split(',')
        .map((n: string) => `filter[name][]=${n}`)
        .join('&');
      url = `/v2/stations/${stationId}/audit/live-mentions/matches?${repeatedNames}`;
    }

    return this.get(url, {
      params: {
        pageSize: pageSize,
        page: page,
        filter: {
          'datetime.>=': from,
          'datetime.<=': to,
        },
        sort: sort || 'datetime desc',
        contain: 'confirmedSchedule',
      },
    });
  },

  getMentionMatch(
    this: BaseAPIClient,
    {
      stationId,
      id,
    }: {
      stationId: string;
      id: string;
    },
  ): Promise<AuditLiveMention> {
    return this.get(`/v2/stations/${stationId}/audit/mentions-matches/${id}`);
  },

  fetchMentionsDateSchedules(
    this: BaseAPIClient,
    args: ApiDataQuery<{
      stationId: string;
      filter: {
        readonly date: DateStr;
      };
    }>,
  ): Promise<Paginated<GetPntScheduleDto>> {
    const { stationId, filter } = args;

    return this.get(`/v2/stations/${stationId}/audit/mentions-date-schedules`, {
      params: {
        date: filter.date,
      },
    });
  },

  updateLiveMentionMatch(
    this: BaseAPIClient,
    args: {
      stationId: string;
      id: string;
      data: Partial<AuditLiveMention>;
    },
  ): Promise<void> {
    const { stationId, id, data } = args;

    return this.put(`/v2/stations/${stationId}/audit/live-mentions/${id}`, data);
  },

  uploadAudio(
    this: BaseAPIClient,
    {
      stationId,
      stationsGroupId,
      file,
      onUploadProgress,
      audioId,
    }: {
      stationId?: string;
      stationsGroupId?: string;
      file: File;
      onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
      audioId?: string;
    },
  ) {
    const data = new FormData();
    data.append('audio', file);

    if (audioId) {
      data.append('customId', audioId);
    }

    if (stationId) {
      data.append('type', 'STATION_AUDIO');
      data.append('stationId', stationId);
    }

    if (stationsGroupId) {
      data.append('type', 'STATIONS_GROUP_AUDIO');
      data.append('stationsGroupId', stationsGroupId);
    }

    const config = {
      onUploadProgress,
    };

    return this.post<AuditAudio & { _overridden?: AuditAudio }>(`/v2/audit/audios`, data, config);
  },

  getDownloadAudioUrl(
    this: BaseAPIClient,
    {
      stationId,
      customId,
      download,
    }: {
      stationId: string;
      customId: string;
      download?: boolean;
    },
  ) {
    return this.generateUrl(
      `/stations/${stationId}/audit/audio/${customId}/file` + (download ? '?download=1' : ''),
      { secure: true },
    );
  },

  async fetchAudios(
    this: BaseAPIClient,
    args: ApiDataQuery<{
      stationId: string;
      filter: {
        from?: DateTimeStr | null;
        to?: DateTimeStr | null;
        customId?: string | string[];
        customIdLike?: string;
      };
    }>,
  ): Promise<Paginated<AuditAudio>> {
    const { stationId, filter, ...query } = args;
    const { from, to, customId, customIdLike } = filter;

    return this.get(`/v2/stations/${stationId}/audit/audios`, {
      params: {
        ...query,
        select: query.select ? query.select.join(',') : undefined,
        filter: {
          customId,
          'customId.like': customIdLike,
          'createdAt.>=': from,
          'createdAt.<=': to,
        },
      },
    });
  },

  fetchSchedules(
    this: BaseAPIClient,
    stationId: string,
    query: ApiDataQuery,
  ): Promise<Paginated<AuditSchedule>> {
    return this.get(`/v2/stations/${stationId}/audit/schedules`, {
      params: query,
    });
  },

  fetchSchedule(this: BaseAPIClient, stationId: string, id: string): Promise<AuditSchedule> {
    return this.get(`/v2/stations/${stationId}/audit/schedules/${id}`, {
      params: {
        contain: 'audio,matches',
      },
    });
  },

  stationReportsEqualMd5(
    this: BaseAPIClient,
    args: {
      stationId: string;
      from: DateTimeStr;
      to: DateTimeStr;
    },
  ): Promise<EqualMd5AudiosReportDto> {
    const { stationId, from, to } = args;

    return this.get(`/v2/stations/${stationId}/audit/reports/equal-md5`, {
      params: {
        from,
        to,
      },
    });
  },

  stationReportsNoMatches(
    this: BaseAPIClient,
    args: {
      stationId: string;
      audioFrom: DateTimeStr;
      audioTo: DateTimeStr;
      matchFrom: DateTimeStr;
      matchTo: DateTimeStr;
    },
  ): Promise<NoMatchesReport> {
    const { stationId, audioFrom, audioTo, matchFrom, matchTo } = args;

    return this.get(`/v2/stations/${stationId}/audit/reports/no-matches`, {
      params: {
        audio_from: audioFrom,
        audio_to: audioTo,
        match_from: matchFrom,
        match_to: matchTo,
      },
    });
  },

  stationReportsMissingAudios(
    this: BaseAPIClient,
    args: {
      stationId: string;
      from: DateTimeStr;
      to: DateTimeStr;
    },
  ): Promise<MissingAudiosReportDto> {
    const { stationId, from, to } = args;

    return this.get(`/v2/stations/${stationId}/audit/reports/missing-audios-schedule`, {
      params: {
        from,
        to,
      },
    });
  },

  stationReportsScheduleStatusAds(
    this: BaseAPIClient,
    stationId: string,
    params: {
      from: DateTimeStr;
      to: DateTimeStr;
      customId?: string;
    },
  ): Promise<ScheduleStatusReport> {
    return this.get(`/v2/stations/${stationId}/audit/reports/schedule-status`, {
      params,
    });
  },

  stationReportsScheduleStatusPnt(
    this: BaseAPIClient,
    params: {
      stationId: string;
      date: DateStr;
      customId?: string;
    },
  ): Promise<PntStationStatusReportDtoV2> {
    return this.post(`/v2/audit/live-mentions/reports/schedule-status-v2`, params);
  },

  reportMissingAudios(
    this: BaseAPIClient,
    data: {
      stationsIds: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      groupBy: string;
      options: {
        format?: string;
        timezone?: string;
      };
    },
  ): Promise<MissingAudiosReportDto> {
    return this.post(`/v2/audit/reports/missing-audios`, data, {
      responseType: data.options.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  reportGlobalNoMatches(
    this: BaseAPIClient,
    data: {
      audioRange: {
        from: DateTimeStr;
        to: DateTimeStr;
      };
      options: {
        format?: string;
      };
    },
  ): Promise<GlobalNoMatchesReportDto> {
    return this.post(`/v2/audit/reports/audios-no-matches`, data, {
      responseType: data.options.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  reportGlobalSchedulesNoMatches(
    this: BaseAPIClient,
    data: {
      stationsIds: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      includeMissingAudio?: boolean;
      page?: number;
      pageSize?: number;
      options: {
        format?: string;
        timezone?: string;
      };
    },
  ): Promise<GlobalSchedulesNoMatchesReport> {
    return this.post(`/v2/audit/reports/schedules-no-matches`, data, {
      responseType: data.options.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  reportGlobalConsecutiveSchedules<T>(
    this: BaseAPIClient,
    data: {
      stationsIds: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      page?: number;
      pageSize?: number;
      options?: {
        format?: string;
      };
    },
  ): Promise<T> {
    return this.post<T>(`/v2/audit/reports/consecutive-schedules`, data, {
      responseType: data.options?.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  async createManualMatch(
    this: BaseAPIClient,
    stationId: string,
    data: CreateManualMatchDto,
  ): Promise<AuditMatch> {
    return this.post(`/v2/stations/${stationId}/audit/matches/create-manual`, data);
  },

  async createManualLiveMention(
    this: BaseAPIClient,
    stationId: string,
    data: CreateLiveMentionManualDto,
  ): Promise<AuditLiveMention> {
    return this.post(`/v2/stations/${stationId}/audit/live-mentions/create-manual`, data);
  },

  plkParse(
    this: BaseAPIClient,
    {
      stationId,
      file,
    }: {
      stationId: string;
      file: File;
    },
  ) {
    const data = new FormData();
    data.append('file', file);

    return this.post<CreateScheduleDtoV2[]>(
      `/v2/stations/${stationId}/audit/schedules/plk-parse`,
      data,
    );
  },

  ascParse(
    this: BaseAPIClient,
    {
      stationId,
      file,
    }: {
      stationId: string;
      file: File;
    },
  ) {
    const data = new FormData();
    data.append('file', file);

    return this.post<CreateScheduleDtoV2[]>(
      `/v2/stations/${stationId}/audit/schedules/asc-parse`,
      data,
    );
  },

  schedulesCreate(
    this: BaseAPIClient,
    {
      stationId,
      data,
    }: {
      stationId: string;
      data: CreateScheduleDtoV2[];
    },
  ) {
    return this.post<AuditSchedule[]>(
      `/v2/stations/${stationId}/audit/schedules/create-batch`,
      data,
    );
  },

  pntSchedulesCsvValidate(
    this: BaseAPIClient,
    {
      stationId,
      file,
    }: {
      stationId: string;
      file: File;
    },
  ) {
    return this.postFile<CreatePntsDto>(
      `/v2/stations/${stationId}/audit/live-mentions/schedules/csv-validate`,
      file,
    );
  },

  pntSchedulesCsvImport(
    this: BaseAPIClient,
    {
      stationId,
      file,
    }: {
      stationId: string;
      file: File;
    },
  ) {
    return this.postFile<CreatePntsDto>(
      `/v2/stations/${stationId}/audit/live-mentions/schedules/csv-import`,
      file,
    );
  },

  configGet(this: BaseAPIClient) {
    return this.get<GetAuditConfigDto>(`/v2/audit/config`);
  },

  configUpdate(this: BaseAPIClient, data: UpdateAuditConfigDto) {
    return this.put<CreatePntsDto>(`/v2/audit/config`, data);
  },

  async fetchLowerthirdRange(
    this: BaseAPIClient,
    slug: string,
    range: { from: number; to: number },
  ): Promise<LowerthirdRangeItem[]> {
    return this.get(`/pnt/v2/segments/${slug}/lowerthird`, {
      params: {
        from: toDateTimeStr(range.from),
        to: toDateTimeStr(range.to),
      },
    });
  },

  async fetchAdoverlayRange(
    this: BaseAPIClient,
    slug: string,
    range: { from: number; to: number },
    withResults: boolean,
    sort?: [{ id: string; desc: boolean }],
  ): Promise<AdoverlayRange[]> {
    return this.get(`/pnt/v2/segments/${slug}/adoverlay`, {
      params: {
        from: toDateTimeStr(range.from),
        to: toDateTimeStr(range.to),
        withResults: withResults,
        sortBy: sort && sort?.length > 0 ? sort[0].id : 'date',
        sortOrder: sort && sort?.length > 0 ? (sort[0].desc ? 'desc' : 'asc') : 'desc',
      },
    });
  },

  async fetchScreenbugRange(
    this: BaseAPIClient,
    slug: string,
    range: { from: number; to: number },
  ): Promise<ScreenbugRangeItem[]> {
    return this.get(`/pnt/v2/segments/${slug}/screenbug`, {
      params: {
        from: toDateTimeStr(range.from),
        to: toDateTimeStr(range.to),
      },
    });
  },

  async fetchYoutubeStats(
    this: BaseAPIClient,
    stationId: string,
    range: { from: number; to: number },
  ): Promise<YoutubeStatsTimePoint[]> {
    return this.get(`/yt-data/v1/viewers`, {
      params: {
        channels: stationId,
        from: toDateTimeStr(range.from),
        to: toDateTimeStr(range.to),
      },
    });
  },

  async fetchYoutubeMetadataByCountry(
    this: BaseAPIClient,
    country: string,
  ): Promise<YoutubeChannelMetadata[]> {
    return this.get(`/yt-data/v1/channels`, {
      params: {
        country: country,
      },
    });
  },

  async gipFetchAdvertisers(
    this: BaseAPIClient,
    params: {
      stations: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      search?: string;
    },
  ): Promise<string[]> {
    return this.get(`/v2/audit/gip/advertisers`, {
      params,
    });
  },

  async gipFetchCampaigns(
    this: BaseAPIClient,
    params: {
      stations: string[];
      advertisers: string[];
      from: DateTimeStr;
      to: DateTimeStr;
    },
  ): Promise<string[]> {
    return this.get(`/v2/audit/gip/campaigns`, {
      params,
    });
  },

  async gipFetchVersions(
    this: BaseAPIClient,
    params: {
      stations: string[];
      advertisers: string[];
      campaigns: string[];
      from: DateTimeStr;
      to: DateTimeStr;
    },
  ): Promise<string[]> {
    return this.get(`/v2/audit/gip/versions`, {
      params,
    });
  },

  async gipFetchCustomFields(
    this: BaseAPIClient,
    params: {
      stations: string[];
    },
  ): Promise<string[]> {
    return this.get(`/v2/audit/gip/custom-fields`, {
      params,
    });
  },

  gipGetParametersForOrderId(
    this: BaseAPIClient,
    params: {
      orderId: string | string[];
      from?: DateTimeStr;
      to?: DateTimeStr;
    },
  ): Promise<GipReportParametersForOrderIdResult> {
    return this.get(`/v2/audit/gip/params-for-order`, {
      params,
    });
  },
};
